import { useEffect, useRef, useState } from 'react';
import { Modal, toast } from '@/components';
import { getSlugFromHostname } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import styles from './PropertyPreviewModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  // 単体モード
  propertyId?: string | null;
  propertyName?: string;
  // 複数モード (提案物件の一括紹介)
  propertyIds?: string[];
  bundleTitle?: string;          // モーダルタイトル補助 例: "3件 / 伊藤 純 様向け"
  bundleDownloadName?: string;   // DL ファイル名 (拡張子なし) 例: "物件紹介書_3件"
}

function buildHeaders(): Record<string, string> {
  const { token, user } = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const slug = getSlugFromHostname() || user?.tenantSlug;
  if (slug) headers['X-Tenant-Slug'] = slug;
  return headers;
}

export function PropertyPreviewModal({ open, onClose, propertyId, propertyName, propertyIds, bundleTitle, bundleDownloadName }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isBundle = Array.isArray(propertyIds) && propertyIds.length > 0;

  const previewUrl = (() => {
    const base = import.meta.env.VITE_API_URL || '/api';
    if (isBundle) return `${base}/tenant/properties/brochure-bundle/preview?ids=${encodeURIComponent(propertyIds!.join(','))}`;
    if (propertyId) return `${base}/tenant/properties/${propertyId}/preview`;
    return null;
  })();

  const pdfUrl = (() => {
    const base = import.meta.env.VITE_API_URL || '/api';
    if (isBundle) return `${base}/tenant/properties/brochure-bundle/pdf?ids=${encodeURIComponent(propertyIds!.join(','))}`;
    if (propertyId) return `${base}/tenant/properties/${propertyId}/pdf`;
    return null;
  })();

  useEffect(() => {
    if (!open || !previewUrl) return;
    setLoading(true);
    fetch(previewUrl, { headers: buildHeaders() })
      .then(async (res) => {
        if (!res.ok) throw new Error('preview failed');
        const html = await res.text();
        if (iframeRef.current) iframeRef.current.srcdoc = html;
      })
      .catch(() => toast('プレビューの取得に失敗しました', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previewUrl]);

  const handlePrint = () => {
    if (!iframeRef.current) return;
    try {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (doc) {
        const bar = doc.querySelector('.print-bar');
        const spacer = doc.querySelector('.print-spacer');
        if (bar) (bar as HTMLElement).style.display = 'none';
        if (spacer) (spacer as HTMLElement).style.display = 'none';
      }
      iframeRef.current.contentWindow?.print();
    } catch {
      const html = iframeRef.current.srcdoc;
      if (html) {
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
      }
    }
  };

  const handleDownload = async () => {
    if (!pdfUrl) return;
    setDownloading(true);
    try {
      const res = await fetch(pdfUrl, { headers: buildHeaders() });
      if (!res.ok) { toast('PDF生成に失敗しました', 'error'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = isBundle
        ? (bundleDownloadName || `物件紹介書_${propertyIds!.length}件`)
        : (propertyName || 'property');
      a.download = `${baseName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast('PDFをダウンロードしました', 'success');
    } catch {
      toast('PDF生成に失敗しました', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const title = isBundle
    ? `📄 物件紹介書 - ${bundleTitle || `${propertyIds!.length}件`}`
    : `📄 物件紹介 - ${propertyName || ''}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={880}
      footer={
        <div className={styles.footer}>
          <button className={styles.btnOutline} onClick={onClose}>閉じる</button>
          <button className={styles.btnPrint} onClick={handlePrint} disabled={loading}>🖨 印刷 / PDF保存</button>
          <button className={styles.btnDownload} onClick={handleDownload} disabled={loading || downloading}>
            {downloading ? 'ダウンロード中...' : '⬇ PDFダウンロード'}
          </button>
        </div>
      }
    >
      <div className={styles.frameWrap}>
        {loading && <div className={styles.overlay}>プレビューを読み込み中...</div>}
        <iframe
          ref={iframeRef}
          className={styles.frame}
          sandbox="allow-same-origin allow-scripts allow-modals"
          title="物件紹介プレビュー"
        />
      </div>
    </Modal>
  );
}
